import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, ShieldAlert, Loader2, Inbox } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_TABS = [
  { value: 'pending', label: 'Pending' },
  { value: 'actioned', label: 'Actioned' },
  { value: 'dismissed', label: 'Dismissed' },
  { value: 'all', label: 'All' },
];

const SEV_COLORS = { low: 'bg-slate-100 text-slate-700', medium: 'bg-amber-100 text-amber-800', high: 'bg-red-100 text-red-700' };
const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-800', reviewed: 'bg-blue-100 text-blue-700',
  actioned: 'bg-emerald-100 text-emerald-800', dismissed: 'bg-slate-100 text-slate-600',
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

const AdminClientConcerns = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('pending');
  const [data, setData] = useState({ concerns: [], pending_count: 0, total_count: 0 });
  const [loading, setLoading] = useState(true);
  const [review, setReview] = useState(null); // the concern being reviewed
  const [action, setAction] = useState('note');
  const [note, setNote] = useState('');
  const [blockClient, setBlockClient] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/client-concerns', { params: { status: tab } });
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load client concerns');
    } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const openReview = (c) => { setReview(c); setAction('note'); setNote(''); setBlockClient(true); };

  const submitReview = async () => {
    if (!review) return;
    if (action === 'note' && !note.trim()) { toast.error('Add a note or choose another action'); return; }
    setSubmitting(true);
    try {
      const payload = { action, note: note.trim() || undefined };
      if (action === 'flag_violation') payload.block_client = blockClient;
      const r = await api.post(`/admin/client-concerns/${review.id}/review`, payload);
      const msgs = {
        dismiss: 'Concern dismissed.',
        note: 'Note saved.',
        flag_violation: r.data.client_blocked ? 'CoC violation flagged and talent blocked from this client.' : 'CoC violation flagged.',
        block_client: 'Talent blocked from this client.',
      };
      toast.success(msgs[action] || 'Concern updated.');
      setReview(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not complete the review');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <button onClick={() => navigate('/admin/code-of-conduct')} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-4" data-testid="concerns-back-btn">
        <ArrowLeft className="w-4 h-4" /> Code of Conduct
      </button>
      <div className="flex items-center gap-2 mb-1">
        <ShieldAlert className="w-6 h-6 text-amber-600" />
        <h1 className="text-2xl font-bold text-slate-900">Client Concerns</h1>
        {data.pending_count > 0 && <Badge className="bg-amber-100 text-amber-800" data-testid="concerns-pending-count">{data.pending_count} pending</Badge>}
      </div>
      <p className="text-sm text-slate-500 mb-4">Concerns reported by clients. Review each one and decide the action — nothing becomes a violation automatically.</p>

      <div className="flex gap-2 mb-4">
        {STATUS_TABS.map((t) => (
          <button key={t.value} onClick={() => setTab(t.value)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium ${tab === t.value ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            data-testid={`concerns-tab-${t.value}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : data.concerns.length === 0 ? (
        <div className="text-center py-16 text-slate-400" data-testid="concerns-empty">
          <Inbox className="w-10 h-10 mx-auto mb-2" />
          <p>No concerns here.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="concerns-list">
          {data.concerns.map((c) => (
            <Card key={c.id} data-testid={`concern-row-${c.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-900">{c.talent_name || 'Talent'}</span>
                      <Badge className={SEV_COLORS[c.severity] || 'bg-slate-100'}>{c.severity}</Badge>
                      <Badge className={STATUS_COLORS[c.status] || 'bg-slate-100'}>{c.status}</Badge>
                      <span className="text-xs text-slate-400">{fmtDate(c.created_at)}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {c.event_title || 'Event'} · reported by {c.client_name || 'client'} · <span className="capitalize">{(c.category || '').replace(/_/g, ' ')}</span>
                    </p>
                    <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{c.description}</p>
                    {Array.isArray(c.attachments) && c.attachments.length > 0 && (
                      <div className="flex gap-2 mt-2">
                        {c.attachments.map((u, i) => (
                          <a key={i} href={u} target="_blank" rel="noreferrer"><img src={u} alt="evidence" className="w-14 h-14 rounded object-cover border" /></a>
                        ))}
                      </div>
                    )}
                    {c.admin_notes && <p className="text-xs text-slate-500 mt-2 whitespace-pre-wrap border-l-2 border-slate-200 pl-2">{c.admin_notes}</p>}
                    {c.linked_violation_id && <p className="text-xs text-emerald-700 mt-1">✓ Linked to a Code of Conduct violation</p>}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => openReview(c)} data-testid={`concern-review-btn-${c.id}`}>
                    Review
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!review} onOpenChange={(v) => { if (!v && !submitting) setReview(null); }}>
        <DialogContent className="max-w-md" data-testid="concern-review-modal">
          <DialogHeader>
            <DialogTitle>Review Concern</DialogTitle>
            <DialogDescription>
              {review?.talent_name} · {review?.event_title}. Choose what to do. Internal notes stay private from the talent and client.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-600">Action</label>
              <Select value={action} onValueChange={setAction}>
                <SelectTrigger data-testid="concern-action-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="note">Add internal note only</SelectItem>
                  <SelectItem value="flag_violation">Flag Code of Conduct violation</SelectItem>
                  <SelectItem value="block_client">Block talent from this client</SelectItem>
                  <SelectItem value="dismiss">Dismiss concern</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {action === 'flag_violation' && (
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer" data-testid="concern-block-toggle">
                <Checkbox checked={blockClient} onCheckedChange={(v) => setBlockClient(!!v)} />
                Also block this talent from this client's future gigs
              </label>
            )}
            <div>
              <label className="text-xs font-semibold text-slate-600">Internal note {action === 'note' ? '(required)' : '(optional)'}</label>
              <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add context for the audit record." data-testid="concern-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReview(null)} disabled={submitting} data-testid="concern-review-cancel-btn">Cancel</Button>
            <Button onClick={submitReview} disabled={submitting} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="concern-review-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminClientConcerns;
